import React, { useState, useEffect } from 'react';
import { architectAPI } from '../services/api';
import toast from 'react-hot-toast';

export default function ApprovedQuestions() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, clat, cuet
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const response = await architectAPI.getApprovedQuestions();
      setQuestions(response.data?.questions || []);
    } catch (error) {
      toast.error(`Failed to load questions: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredQuestions = questions
    .filter(q => {
      const matchesExam = filter === 'all' || q.exam === filter.toUpperCase();
      const matchesSearch = !searchTerm || 
        q.question_text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.source_article?.title?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesExam && matchesSearch;
    });

  const exportQuestions = () => {
    const dataStr = JSON.stringify(filteredQuestions, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `approved_questions_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    toast.success('Questions exported successfully');
  };

  return (
    <div className="min-h-screen">
      {/* Modern Header */}
      <div className="relative bg-gradient-to-r from-green-600 via-emerald-700 to-teal-800 rounded-3xl shadow-2xl p-8 mb-8 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>
        
        <div className="relative flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
              <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-1">Approved Questions ✅</h2>
              <p className="text-white/90">
                Production-ready questions • {filteredQuestions.length} total
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportQuestions}
              disabled={filteredQuestions.length === 0}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-semibold shadow-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="h-5 w-5 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export
            </button>
            <button
              onClick={loadQuestions}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-semibold shadow-lg transition-all hover:scale-105"
            >
              <svg className="h-5 w-5 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white shadow-xl rounded-2xl p-6 mb-8 border border-gray-100">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-64">
            <label className="block text-sm font-semibold text-gray-700 mb-2">🔍 Search Questions</label>
            <input
              type="text"
              placeholder="Search by question text or article title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full rounded-xl border-2 border-gray-200 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-200 text-sm px-4 py-3 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">📚 Filter by Exam</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="rounded-xl border-2 border-gray-200 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-200 text-sm px-4 py-3 transition-all"
            >
              <option value="all">All Exams</option>
              <option value="clat">CLAT Only</option>
              <option value="cuet">CUET Only</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-green-600"></div>
            <div className="absolute top-0 left-0 h-16 w-16 rounded-full bg-green-100"></div>
          </div>
        </div>
      ) : filteredQuestions.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
          <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-full p-6 inline-block mb-4">
            <svg className="h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No approved questions</h3>
          <p className="text-gray-500 mb-6">
            Review and approve questions from the Pending Questions page
          </p>
          <button
            onClick={() => window.location.href = '/pending-questions'}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg transition-all"
          >
            Go to Pending Questions
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 rounded-2xl p-6 shadow-lg border border-green-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-green-600 rounded-xl p-3">
                  <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  📊 Showing <span className="text-green-600">{filteredQuestions.length}</span> approved question(s)
                </span>
              </div>
            </div>
          </div>

          {filteredQuestions.map((question, idx) => (
            <div key={question._id || idx} className="group bg-white shadow-xl rounded-2xl p-6 border border-gray-100 hover:border-green-200 transition-all hover:shadow-2xl">
              {/* Question Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold text-sm">
                    {idx + 1}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm">
                    {question.exam}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold shadow-sm ${
                    question.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                    question.difficulty === 'Hard' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {question.difficulty || 'Medium'}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200">
                    ✅ Approved
                  </span>
                </div>
                <span className="text-sm text-gray-500 flex items-center">
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {question.approved_at ? new Date(question.approved_at).toLocaleDateString() : ''}
                </span>
              </div>

              {/* Question Text */}
              <div className="mb-4">
                <p className="text-base font-semibold text-gray-900 leading-relaxed">{question.question_text}</p>
              </div>

              {/* Options */}
              <div className="space-y-3 mb-4">
                {question.options?.map((option, optIdx) => (
                  <div
                    key={optIdx}
                    className={`group/option flex items-start p-4 rounded-xl border-2 transition-all ${
                      optIdx === question.correct_answer_index
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 shadow-md'
                        : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg font-bold mr-3 ${
                      optIdx === question.correct_answer_index
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 group-hover/option:bg-gray-200'
                    }`}>
                      {String.fromCharCode(65 + optIdx)}
                    </span>
                    <span className="text-gray-700 flex-1 leading-relaxed">{option}</span>
                    {optIdx === question.correct_answer_index && (
                      <div className="flex-shrink-0 ml-2">
                        <div className="bg-green-600 rounded-full p-1">
                          <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Explanation */}
              {question.explanation && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100 mb-4">
                  <div className="flex items-start">
                    <div className="bg-blue-600 rounded-lg p-2 mr-3">
                      <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-gray-900 mb-1">💡 Explanation:</p>
                      <p className="text-sm text-gray-700 leading-relaxed">{question.explanation}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Source Article */}
              {question.source_article && (
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-start text-sm text-gray-600">
                    <svg className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                    </svg>
                    <div>
                      <span className="font-medium text-gray-700">Source: </span>
                      {question.source_article.title}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
